import { NavLink } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightStartOnRectangleIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { HandRaisedIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext';
import { useConversations } from '@/context/ConversationsContext';
import { usePrayerUnreadContext } from '@/context/PrayerUnreadContext';
import { usePollBadgesContext } from '@/context/PollBadgesContext';
import { useOrgLockState } from '@/hooks/useOrgLockState';
import PausedChip from '@/components/lock/PausedChip';

const NAV_ITEMS = [
  { to: '/chat', label: 'Chat', icon: ChatBubbleLeftRightIcon },
  { to: '/prayers', label: 'Prayers', icon: HandRaisedIcon },
  { to: '/events', label: 'Events', icon: CalendarDaysIcon },
  { to: '/polls', label: 'Polls', icon: ChartBarIcon },
  { to: '/signups', label: 'Sign-Ups', icon: ClipboardDocumentListIcon },
  { to: '/roster', label: 'Roster', icon: UsersIcon },
];

function initials(name?: string | null): string {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Read-only org/group indicator at the top of the sidebar. This used to be
 * the interactive ContextSwitcher dropdown — now every tab (chat, prayers,
 * events, roster) navigates orgs and groups inline from its own rail, which
 * rotates the org context automatically. This block just reflects where you
 * are as you move around.
 */
function ContextDisplay() {
  const { currentOrganization, currentGroup, isSwitchingOrg } = useAuth();
  const { isOrgLocked } = useOrgLockState();
  const locked = isOrgLocked(currentOrganization?.id);
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      {currentOrganization?.logo_url ? (
        <img
          src={currentOrganization.logo_url}
          alt=""
          className="h-9 w-9 shrink-0 rounded-lg bg-white object-cover"
        />
      ) : (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-flock-navy-600 text-sm font-semibold text-white">
          {initials(currentOrganization?.name)}
        </div>
      )}
      <div className="min-w-0 flex-1 max-lg:hidden">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-white">
            {isSwitchingOrg ? 'Switching…' : (currentOrganization?.name ?? 'Flock')}
          </span>
          {locked && !isSwitchingOrg && <PausedChip />}
        </div>
        <div className="truncate text-xs text-flock-navy-300">{currentGroup?.name ?? '—'}</div>
      </div>
    </div>
  );
}

export default function Sidebar() {
  const { user, signOut } = useAuth();
  const { totalUnread: prayerUnread } = usePrayerUnreadContext();
  const { totalUnread: chatUnread } = useConversations();
  const { totalOpen: pollsOpen, totalUnvoted: pollsUnvoted } = usePollBadgesContext();
  // Red = open polls awaiting your vote (count of those); muted = all participated
  const pollBadgeCount = pollsUnvoted > 0 ? pollsUnvoted : pollsOpen;

  return (
    <aside className="flex h-full w-16 shrink-0 flex-col bg-flock-navy-900 lg:w-72">
      <div className="p-3">
        <ContextDisplay />
      </div>

      <nav className="flex-1 space-y-1 px-3">
        {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              clsx(
                'min-h-touch flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition',
                isActive
                  ? 'bg-flock-navy-600 text-white'
                  : 'text-flock-navy-200 hover:bg-flock-navy-800 hover:text-white'
              )
            }
          >
            <div className="relative shrink-0">
              <Icon className="h-5 w-5" />
              {to === '/chat' && chatUnread > 0 && (
                <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#E63946] px-1 text-[10px] font-bold leading-none text-white ring-[1.5px] ring-flock-navy-900">
                  {chatUnread > 99 ? '99+' : chatUnread}
                </span>
              )}
              {to === '/prayers' && prayerUnread > 0 && (
                // Expo tab-badge parity: red pill, navy ring, 99+ cap
                <span className="absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#E63946] px-1 text-[10px] font-bold leading-none text-white ring-[1.5px] ring-flock-navy-900">
                  {prayerUnread > 99 ? '99+' : prayerUnread}
                </span>
              )}
              {to === '/polls' && pollsOpen > 0 && (
                <span
                  className={clsx(
                    'absolute -right-2 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white ring-[1.5px] ring-flock-navy-900',
                    pollsUnvoted > 0 ? 'bg-[#E63946]' : 'bg-flock-navy-500'
                  )}
                >
                  {pollBadgeCount > 99 ? '99+' : pollBadgeCount}
                </span>
              )}
            </div>
            <span className="max-lg:hidden">{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="border-t border-flock-navy-800 p-3">
        <div className="flex items-center gap-3 px-1">
          {user?.profile_image_url ? (
            <img src={user.profile_image_url} alt="" className="h-9 w-9 rounded-full object-cover" />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-flock-navy-600 text-sm font-semibold text-white">
              {initials(user?.name)}
            </div>
          )}
          <div className="min-w-0 flex-1 max-lg:hidden">
            <div className="truncate text-sm font-medium text-white">{user?.name}</div>
            <div className="truncate text-xs text-flock-navy-300">{user?.email}</div>
          </div>
          <button
            onClick={() => signOut()}
            title="Sign out"
            className="rounded-lg p-2 text-flock-navy-300 transition hover:bg-flock-navy-800 hover:text-white max-lg:hidden"
          >
            <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </aside>
  );
}
